import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Image, Rect, Text } from 'react-konva';
import Konva from 'konva';
import type { Node } from '../../../core/models/scene';
import type { OrgReportCounts } from '../services/orgChartReportCounts';
import type { NodeScenarioStatus } from '../models/Scenario';
import { buildAccentGradientStops, resolveOrgAccentColor, resolveOrgBorderColor } from './orgChartShapeColors';
import {
  getScenarioStatusStyle,
  shouldShowScenarioStyling,
  getKonvaDash,
  getScenarioStrokeColor,
  getScenarioStrokeWidth,
  getScenarioFillColor,
  getScenarioOpacity,
} from './scenarioStatusStyles';
import { User, Users } from 'lucide-react';
import { useLucideIconImage } from './orgChartFooterIcons';
import { useHighlightStore } from '../../../stores/highlightStore';
import type { ComputedNodeStyle } from '../hooks/useViewRules';
import { DisplayRuleBadges } from './DisplayRuleBadge';
import { OrgRolesChip } from './OrgRolesChip';
import { rolesChipReserve } from '../models/personRoles';
import { DisplayRuleLabels } from './DisplayRuleLabel';
import { resolveOrgTextStyle } from './orgChartTextStyles';
import { layoutOrgCardDetailRows } from './orgCardDetailLayout';
import { openOrgCardProperties } from './openOrgCardProperties';
import { useAnimatedLayoutPosition } from '../hooks/useAnimatedLayoutPosition';
import { useOrgNodeEntrance } from '../hooks/useOrgNodeEntrance';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import { useDiagramSettings } from '../../../hooks/useDiagramSettings';
import { resolveNodeCustomFields } from './orgCardFields';
import { asTouchKonvaHandler } from '../../../lib/konvaEventAdapters';
import { useTranslation } from '../../../hooks/useTranslation';
import { getOrgFilterDimPalette } from './orgChartDimStyles';

const getThemeColor = (name: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const createRoundedTopClip = (width: number, height: number, cornerRadius: number) => {
  return (ctx: any) => {
    ctx.beginPath();
    ctx.moveTo(cornerRadius, 0);
    ctx.lineTo(width - cornerRadius, 0);
    ctx.quadraticCurveTo(width, 0, width, cornerRadius);
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.lineTo(0, cornerRadius);
    ctx.quadraticCurveTo(0, 0, cornerRadius, 0);
    ctx.closePath();
  };
};

interface OrgPersonNodeProps {
  node: Node;
  selected: boolean;
  reportCounts?: OrgReportCounts;
  collapseTargetId?: string;
  onToggleCollapse?: (reportingNodeId: string) => void;
  enableCollapseAnimation?: boolean;
  isExiting?: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onDoubleClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onTransformStart?: (e: Konva.KonvaEventObject<Event>) => void;
  onTransformEnd?: (e: Konva.KonvaEventObject<Event>) => void;
  onTransform?: (e: Konva.KonvaEventObject<Event>) => void;
  onStartTextEdit?: (textNode: Konva.Text) => void;
  editingPrimaryLabel?: boolean;
  canWrite?: boolean;
  canDrag?: boolean;
  /** Scenario status for visual diff rendering */
  scenarioStatus?: NodeScenarioStatus;
  viewRuleState?: {
    opacity: number;
    visible: boolean;
    style: ComputedNodeStyle;
  };
  renderDisplayRuleLabels?: boolean;
}

const OrgPersonNodeInner = forwardRef<Konva.Group, OrgPersonNodeProps>(({
  node,
  selected,
  reportCounts,
  collapseTargetId,
  onToggleCollapse,
  enableCollapseAnimation,
  isExiting,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick,
  onTransformStart,
  onTransformEnd,
  onTransform,
  onStartTextEdit,
  editingPrimaryLabel,
  canWrite = true,
  canDrag = canWrite,
  scenarioStatus = 'unchanged',
  viewRuleState,
  renderDisplayRuleLabels = true,
}, ref) => {
  const { t } = useTranslation('diagram');
  const { doc } = useDiagramDocument();
  const { read: readSettings } = useDiagramSettings(doc);
  const settings = readSettings();
  const customFieldDefinitions = settings.orgchart?.customFieldDefinitions ?? [];
  const groupRef = useRef<Konva.Group | null>(null);
  const nameTextRef = useRef<Konva.Text | null>(null);

  const width = (node.w ?? node.width ?? 180) as number;
  const height = (node.h ?? node.height ?? 70) as number;

  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const props = (node.props as Record<string, unknown>) ?? {};

  const fillColor = (node.fillColor as string) || (attrs.fillColor as string) || '#FFFFFF';
  const textColor = (attrs.textColor as string) || (props.textColor as string) || '#111827';

  const defaultPersonName = t('orgchart.defaultPersonName', { defaultValue: 'New Person' }) as string;
  const name = (attrs.name as string) || (props.name as string) || (node.name as string) || defaultPersonName;
  const role = (attrs.role as string) || (attrs.title as string) || (props.role as string) || '';
  const email = (attrs.email as string) || (props.email as string) || '';
  const subtitle = role || email;

  const cornerRadius = 12; // rounded-xl
  const accentHeight = 4;
  const paddingX = 16;
  const paddingTop = 20;
  const lineHeight = 14;

  // Check highlight state from store
  const highlightedIds = useHighlightStore((s) => s.highlightedIds);
  const reportingChainIds = useHighlightStore((s) => s.reportingChainIds);
  const highlightColor = useHighlightStore((s) => s.highlightColor);
  
  const isPrimaryHighlight = highlightedIds.has(node.id);
  const isChainHighlight = reportingChainIds.has(node.id);

  // Scenario status styling takes precedence over selection and highlights
  const showScenarioStyling = shouldShowScenarioStyling(scenarioStatus);
  const scenarioStyle = getScenarioStatusStyle(scenarioStatus);
  const scenarioDash = getKonvaDash(scenarioStatus);
  const scenarioFillColor = getScenarioFillColor(scenarioStatus, fillColor);
  const scenarioOpacity = getScenarioOpacity(scenarioStatus);

  const filterOpacity = viewRuleState?.opacity ?? 1;

  // Get display rule styles (color overrides)
  const displayRuleStyle = viewRuleState?.style ?? { filterOpacity: 1, filterVisible: true, badges: [], labels: [] };
  const displayBgColor = displayRuleStyle.backgroundColor;
  const displayBorderColor = displayRuleStyle.borderColor;
  const displayTextColor = displayRuleStyle.textColor;
  const accentColor = resolveOrgAccentColor(attrs, props, node.strokeColor as string);
  const effectiveAccentColor = displayBorderColor || accentColor;
  const subtitleColor = displayTextColor || effectiveAccentColor;
  const nameTextStyle = resolveOrgTextStyle(attrs, { fontStyle: 'bold' }, (node as { fontFamily?: string }).fontFamily);
  const subtitleTextStyle = resolveOrgTextStyle(attrs, { fontStyle: 'normal' }, (node as { fontFamily?: string }).fontFamily);
  const fontFamily = nameTextStyle.fontFamily ?? 'Inter, system-ui, -apple-system, sans-serif';

  const baseBorderColor = resolveOrgBorderColor(attrs, props, node.strokeColor as string);

  let strokeColor = 'transparent';
  let strokeWidth = 0;

  if (showScenarioStyling) {
    strokeColor = getScenarioStrokeColor(scenarioStatus, selected);
    strokeWidth = getScenarioStrokeWidth(scenarioStatus, selected);
  } else if (selected) {
    strokeColor = '#3B82F6';
    strokeWidth = 2;
  } else if (isPrimaryHighlight) {
    strokeColor = highlightColor;
    strokeWidth = 4;
  } else if (isChainHighlight) {
    strokeColor = highlightColor;
    strokeWidth = 2;
  } else if (displayBorderColor) {
    strokeColor = displayBorderColor;
    strokeWidth = 2;
  } else if (baseBorderColor) {
    strokeColor = baseBorderColor;
    strokeWidth = 1;
  }

  const accentGradientStops = buildAccentGradientStops(effectiveAccentColor, 'bar');
  const canvasBackgroundColor = getThemeColor('--background', '#FFFFFF');
  const dimPalette = useMemo(() => getOrgFilterDimPalette({
    opacity: filterOpacity,
    backgroundColor: canvasBackgroundColor,
    fillColor: showScenarioStyling ? scenarioFillColor : (displayBgColor || fillColor),
    strokeColor,
    primaryTextColor: displayTextColor || textColor || '#111827',
    secondaryTextColor: subtitleColor || '#6B7280',
    detailLabelColor: displayTextColor || textColor || '#111827',
    detailValueColor: displayTextColor || textColor || '#111827',
    accentColor: effectiveAccentColor,
  }), [
    filterOpacity,
    canvasBackgroundColor,
    showScenarioStyling,
    scenarioFillColor,
    displayBgColor,
    fillColor,
    strokeColor,
    displayTextColor,
    textColor,
    subtitleColor,
    effectiveAccentColor,
  ]);
  const isFilterDimmed = dimPalette.isDimmed;

  const directReports = reportCounts?.directReports ?? 0;
  const totalReports = reportCounts?.totalReports ?? 0;
  const showFooter = directReports > 0 || totalReports > 0;
  const footerHeight = showFooter ? 26 : 0;
  const footerPaddingX = 16;
  const footerLabelFontSize = 12;
  const footerValueFontSize = 12;
  const [hoveredFooter, setHoveredFooter] = useState<'direct' | 'total' | null>(null);
  const tooltipBg = useMemo(() => getThemeColor('--primary', '#08436B'), []);
  const tooltipFg = useMemo(() => getThemeColor('--primary-foreground', '#FFFFFF'), []);
  const footerIconSize = 12;
  const directIcon = useLucideIconImage(User, footerIconSize, '#6B7280');
  const totalIcon = useLucideIconImage(Users, footerIconSize, '#6B7280');
  const handleFooterClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!onToggleCollapse || !collapseTargetId) return;
    e.cancelBubble = true;
    onToggleCollapse(collapseTargetId);
  };

  const visibleCustomFields = useMemo(() => {
    const rawFields = resolveNodeCustomFields({
      nodeType: node.type,
      attrs,
      props,
      definitions: customFieldDefinitions,
    });
    return rawFields.filter((field) => field?.showOnCard && field?.name);
  }, [node.type, attrs, props, customFieldDefinitions]);

  const extraStartY = paddingTop + 40;
  const extraLayout = useMemo(() => {
    if (visibleCustomFields.length === 0) return { rows: [], hiddenRows: 0 };
    const rows = visibleCustomFields.map((field) => ({
      id: String(field.id || field.name),
      label: String(field.name ?? ''),
      value: String(field.value ?? ''),
    }));
    const available = Math.max(0, height - extraStartY - paddingTop - footerHeight);
    return layoutOrgCardDetailRows({
      rows,
      valueWidth: width - paddingX * 2,
      fontSize: 12,
      lineHeightPx: lineHeight,
      availableHeightPx: available,
      maxLinesPerRow: 1,
      rowGapPx: 2,
    });
  }, [visibleCustomFields, height, extraStartY, paddingTop, footerHeight, width, paddingX, lineHeight]);
  const extraLines = extraLayout.rows;
  const viewMoreY = useMemo(() => {
    const lastRow = extraLines[extraLines.length - 1];
    const desiredY = lastRow ? extraStartY + lastRow.y + lastRow.height + 2 : extraStartY;
    const maxY = Math.max(extraStartY, height - footerHeight - lineHeight - 2);
    return Math.min(desiredY, maxY);
  }, [extraLines, extraStartY, height, footerHeight, lineHeight]);

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!handler) return;
    const pos = e.target.position();
    handler({ x: pos.x, y: pos.y });
  };

  const isFooterHit = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!showFooter) return false;
    const stage = e.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (!pointer || !groupRef.current) return false;
    const transform = groupRef.current.getAbsoluteTransform().copy().invert();
    const local = transform.point(pointer);
    return local.y >= height - footerHeight;
  };

  const handleDoubleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // If external double-click handler is provided, call it first
    if (onDoubleClick) {
      onDoubleClick(e);
      // If the event was prevented, don't do default text editing
      if (e.evt?.defaultPrevented) {
        return;
      }
    }
    if (!canWrite) return;
    if (isFooterHit(e)) return;
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    e.evt?.preventDefault?.();
    if (onStartTextEdit && nameTextRef.current) {
      onStartTextEdit(nameTextRef.current);
    }
  };
  const handleViewMoreClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    e.evt?.preventDefault?.();
    openOrgCardProperties(node.id);
  };
  const handleViewMorePointerDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    e.evt?.preventDefault?.();
  };
  const handleViewMorePointerEnter = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    const container = stage?.container();
    if (container) container.style.cursor = 'pointer';
  };
  const handleViewMorePointerLeave = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    const container = stage?.container();
    if (container) container.style.cursor = 'default';
  };

  // Animated position transitions
  const suppressLayoutAnim = useAnimatedLayoutPosition(
    groupRef, node.x, node.y,
    { enabled: !!enableCollapseAnimation && !isExiting },
  );

  // Collapse highlight: flash the parent node when a branch is collapsed
  const pendingCollapseHL = useOrgChartViewStore(s => s.pendingCollapseHighlight);
  const collapseHL = !!pendingCollapseHL && pendingCollapseHL === collapseTargetId;
  // Outline-first entrance animation + collapse highlight
  const entrance = useOrgNodeEntrance(
    !!enableCollapseAnimation,
    !!isExiting,
    width,
    height,
    cornerRadius,
    collapseHL,
  );

  // Exit animation (unchanged — fades entire outer Group)
  const didExitRef = useRef(false);
  useEffect(() => {
    if (!enableCollapseAnimation || !isExiting || didExitRef.current) return;
    const nodeEl = groupRef.current;
    if (!nodeEl) return;
    didExitRef.current = true;
    nodeEl.opacity(1);
    const tween = new Konva.Tween({
      node: nodeEl,
      duration: 0.15,
      opacity: 0,
    });
    tween.play();
    return () => {
      tween.destroy();
    };
  }, [enableCollapseAnimation, isExiting]);

  useEffect(() => {
    const contentEl = entrance.contentGroupRef.current;
    if (!contentEl) return;

    try {
      if (editingPrimaryLabel || hoveredFooter !== null || isExiting) {
        contentEl.clearCache();
        contentEl.getLayer()?.batchDraw();
        return;
      }

      contentEl.clearCache();
      contentEl.cache({ pixelRatio: 1 });
      contentEl.getLayer()?.batchDraw();
    } catch {}

    return () => {
      try {
        contentEl.clearCache();
      } catch {}
    };
  }, [
    entrance.contentGroupRef,
    node,
    selected,
    reportCounts,
    visibleCustomFields,
    extraLayout.rows,
    extraLayout.hiddenRows,
    displayRuleStyle.badges,
    displayRuleStyle.labels,
    editingPrimaryLabel,
    hoveredFooter,
    isExiting,
    scenarioStatus,
    viewRuleState,
    showFooter,
  ]);

  // Hide mode: don't render non-matching nodes
  if (viewRuleState && !viewRuleState.visible) {
    return null;
  }

  return (
    <Group
      ref={(nodeEl) => {
        groupRef.current = nodeEl;
        if (typeof ref === 'function') ref(nodeEl);
        else if (ref && typeof ref === 'object') (ref as React.MutableRefObject<Konva.Group | null>).current = nodeEl;
      }}
      id={node.id}
      name="shape"
      x={node.x}
      y={node.y}
      width={width}
      height={height}
      draggable={canDrag && !isExiting && !editingPrimaryLabel}
      listening={!isExiting}
      onDragStart={(e) => { suppressLayoutAnim.current = true; fireDrag(onDragStart)(e); }}
      onDragMove={fireDrag(onDragMove)}
      onDragEnd={(e) => { fireDrag(onDragEnd)(e); suppressLayoutAnim.current = false; }}
      onClick={onClick}
      onTap={asTouchKonvaHandler(onClick)}
      onTransformStart={onTransformStart}
      onTransformEnd={onTransformEnd}
      onTransform={onTransform}
      onDblClick={handleDoubleClick}
      opacity={scenarioOpacity}
    >
      {entrance.outlineProps && <Rect {...entrance.outlineProps} />}
      <Group ref={entrance.contentGroupRef}>
      {/* Card background */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={isFilterDimmed ? dimPalette.fillColor : (showScenarioStyling ? scenarioFillColor : (displayBgColor || fillColor))}
        stroke={isFilterDimmed ? dimPalette.strokeColor : strokeColor}
        strokeWidth={strokeWidth}
        cornerRadius={cornerRadius}
        shadowEnabled={false}
        dash={scenarioDash}
      />

      {/* Scenario status badge */}
      {showScenarioStyling && (
        <Group x={width - 28} y={8}>
          <Rect
            width={20}
            height={20}
            fill={scenarioStyle.badgeBackground}
            cornerRadius={10}
          />
          <Text
            width={20}
            height={20}
            text={scenarioStyle.icon}
            fontSize={14}
            fontStyle="bold"
            fill={scenarioStyle.badgeTextColor}
            align="center"
            verticalAlign="middle"
            listening={false}
          />
        </Group>
      )}

      {/* Top gradient accent bar with rounded top corners using clipping mask */}
      <Group
        clipFunc={createRoundedTopClip(width, accentHeight, cornerRadius)}
        listening={false}
      >
        <Rect
          x={0}
          y={0}
          width={width}
          height={accentHeight}
          fill={isFilterDimmed ? dimPalette.accentColor : undefined}
          fillLinearGradientStartPoint={isFilterDimmed ? undefined : { x: 0, y: 0 }}
          fillLinearGradientEndPoint={isFilterDimmed ? undefined : { x: width, y: 0 }}
          fillLinearGradientColorStops={isFilterDimmed ? undefined : accentGradientStops}
          listening={false}
        />
      </Group>

      {/* Name text */}
      <Text
        ref={nameTextRef}
        x={paddingX}
        y={paddingTop}
        width={Math.max(0, width - paddingX * 2 - rolesChipReserve(attrs))}
        height={17}
        text={name}
        fontSize={13}
        fontFamily={fontFamily}
        lineHeight={17 / 13}
        wrap="none"
        fontStyle={nameTextStyle.fontStyle}
        textDecoration={nameTextStyle.textDecoration}
        fill={isFilterDimmed ? dimPalette.primaryTextColor : (displayTextColor || textColor)}
        visible={!editingPrimaryLabel}
        listening={false}
        ellipsis
      />

      {/* Subtitle text */}
      {subtitle && (
        <Text
          x={paddingX}
          y={paddingTop + 17}
          width={width - paddingX * 2}
          height={13}
          text={subtitle}
          fontSize={11}
          fontFamily={fontFamily}
          lineHeight={13 / 11}
          wrap="none"
          fontStyle={subtitleTextStyle.fontStyle}
          textDecoration={subtitleTextStyle.textDecoration}
          fill={isFilterDimmed ? dimPalette.secondaryTextColor : subtitleColor}
          listening={false}
          ellipsis
        />
      )}

      {extraLines.map((field) => (
        <Text
          key={field.id}
          x={paddingX}
          y={extraStartY + field.y}
          width={width - paddingX * 2}
          height={field.height}
          text={`${field.label}: ${field.value}`}
          fontSize={12}
          fontFamily={fontFamily}
          lineHeight={lineHeight / 12}
          wrap="none"
          fill={isFilterDimmed ? dimPalette.detailValueColor : textColor}
          listening={false}
          ellipsis
        />
      ))}
      {extraLayout.hiddenRows > 0 && (
        <Text
          x={paddingX}
          y={viewMoreY}
          width={width - paddingX * 2}
          height={lineHeight}
          text="View more.."
          fontSize={12}
          fontFamily={fontFamily}
          lineHeight={lineHeight / 12}
          fontStyle="normal"
          fill={isFilterDimmed ? dimPalette.secondaryTextColor : '#6B7280'}
          onMouseDown={handleViewMorePointerDown}
          onTouchStart={handleViewMorePointerDown}
          onClick={handleViewMoreClick}
          onTap={asTouchKonvaHandler(handleViewMoreClick)}
          onMouseEnter={handleViewMorePointerEnter}
          onMouseLeave={handleViewMorePointerLeave}
        />
      )}

      <OrgRolesChip attrs={attrs} cardWidth={width} />

      {/* Display rule badges */}
      {displayRuleStyle.badges.length > 0 && (
        <DisplayRuleBadges
          badges={displayRuleStyle.badges}
          nodeWidth={width}
          nodeHeight={height}
        />
      )}

      {/* Display rule labels */}
      {renderDisplayRuleLabels && displayRuleStyle.labels.length > 0 && (
        <DisplayRuleLabels
          labels={displayRuleStyle.labels}
          nodeWidth={width}
          nodeHeight={height}
        />
      )}

      {showFooter && (
        <>
          <Rect
            x={footerPaddingX}
            y={height - footerHeight}
            width={width - footerPaddingX * 2}
            height={1}
            fill={isFilterDimmed ? dimPalette.separatorColor : '#E5E7EB'}
            listening={false}
          />
          <Group y={height - footerHeight + 6}>
            {(() => {
              const leftX = footerPaddingX;
              const gap = 16;
              const rowHeight = footerHeight - 10;
              const rowCenterY = rowHeight / 2;
              const tooltipPaddingX = 8;
              const tooltipPaddingY = 8;
              const tooltipArrowSize = 8;
              const directLabel = t('orgchart.nodeFooter.directReports', { defaultValue: 'Direct reports' });
              const totalLabel = t('orgchart.nodeFooter.totalReports', { defaultValue: 'Total reports' });
              const estimateTooltipWidth = (label: string) => (
                Math.max(64, label.length * (footerLabelFontSize * 0.55) + tooltipPaddingX * 2)
              );
              const directTooltipWidth = estimateTooltipWidth(directLabel);
              const totalTooltipWidth = estimateTooltipWidth(totalLabel);
              const tooltipHeight = footerLabelFontSize + tooltipPaddingY * 2;
              const directValueX = leftX + footerIconSize + 6;
              const directTextWidth = String(directReports).length * (footerValueFontSize * 0.6);
              const directGroupWidth = footerIconSize + 6 + directTextWidth;
              const totalIconX = leftX + directGroupWidth + gap;
              const totalValueX = totalIconX + footerIconSize + 6;
              const totalTextWidth = String(totalReports).length * (footerValueFontSize * 0.6);
              const totalGroupWidth = footerIconSize + 6 + totalTextWidth;
              const directHitWidth = directGroupWidth + 8;
              const totalHitWidth = totalGroupWidth + 8;
              return (
                <>
                  <Rect
                    x={leftX}
                    y={0}
                    width={directHitWidth}
                    height={footerHeight - 6}
                    fill="transparent"
                    onClick={handleFooterClick}
                    onTap={asTouchKonvaHandler(handleFooterClick)}
                    onMouseEnter={(e) => {setHoveredFooter('direct'); const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'pointer'; }}
                    onMouseLeave={(e) => { setHoveredFooter(null); const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'default'; }}
                  />
                  {directIcon && (
                    <Image
                      image={directIcon}
                      x={leftX}
                      y={rowCenterY - footerIconSize / 2}
                      width={footerIconSize}
                      height={footerIconSize}
                      opacity={dimPalette.iconOpacity}
                      listening={false}
                    />
                  )}
                  <Text
                    x={directValueX}
                    y={rowCenterY - footerValueFontSize / 2}
                    width={directGroupWidth}
                    text={String(directReports)}
                    fontSize={footerValueFontSize}
                    fontFamily={fontFamily}
                    fontStyle="bold"
                    fill={isFilterDimmed ? dimPalette.primaryTextColor : '#111827'}
                    listening={false}
                  />
                  {hoveredFooter === 'direct' && (
                    <Group
                      x={leftX + directHitWidth / 2 - directTooltipWidth / 2}
                      y={-tooltipHeight - tooltipArrowSize}
                      listening={false}
                    >
                      <Rect
                        width={directTooltipWidth}
                        height={tooltipHeight}
                        fill={tooltipBg}
                        cornerRadius={6}
                      />
                      <Rect
                        x={directTooltipWidth / 2}
                        y={tooltipHeight - tooltipArrowSize / 2}
                        width={tooltipArrowSize}
                        height={tooltipArrowSize}
                        fill={tooltipBg}
                        rotation={45}
                        offsetX={tooltipArrowSize / 2}
                        offsetY={tooltipArrowSize / 2}
                      />
                      <Text
                        x={tooltipPaddingX}
                        y={0}
                        width={directTooltipWidth - tooltipPaddingX * 2}
                        height={tooltipHeight}
                        text={directLabel}
                        fontSize={footerLabelFontSize}
                        fontFamily={fontFamily}
                        fill={tooltipFg}
                        align="center"
                        verticalAlign="middle"
                      />
                    </Group>
                  )}

                  <Rect
                    x={totalIconX}
                    y={0}
                    width={totalHitWidth}
                    height={footerHeight - 6}
                    fill="transparent"
                    onClick={handleFooterClick}
                    onTap={asTouchKonvaHandler(handleFooterClick)}
                    onMouseEnter={(e) => {setHoveredFooter('total'); const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'pointer'; }}
                    onMouseLeave={(e) => { setHoveredFooter(null); const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'default'; }}
                  />
                  {totalIcon && (
                    <Image
                      image={totalIcon}
                      x={totalIconX}
                      y={rowCenterY - footerIconSize / 2}
                      width={footerIconSize}
                      height={footerIconSize}
                      opacity={dimPalette.iconOpacity}
                      listening={false}
                    />
                  )}
                  <Text
                    x={totalValueX}
                    y={rowCenterY - footerValueFontSize / 2}
                    width={totalGroupWidth}
                    text={String(totalReports)}
                    fontSize={footerValueFontSize}
                    fontFamily={fontFamily}
                    fontStyle="bold"
                    fill={isFilterDimmed ? dimPalette.primaryTextColor : '#111827'}
                    listening={false}
                  />
                  {hoveredFooter === 'total' && (
                    <Group
                      x={totalIconX + totalHitWidth / 2 - totalTooltipWidth / 2}
                      y={-tooltipHeight - tooltipArrowSize}
                      listening={false}
                    >
                      <Rect
                        width={totalTooltipWidth}
                        height={tooltipHeight}
                        fill={tooltipBg}
                        cornerRadius={6}
                      />
                      <Rect
                        x={totalTooltipWidth / 2}
                        y={tooltipHeight - tooltipArrowSize / 2}
                        width={tooltipArrowSize}
                        height={tooltipArrowSize}
                        fill={tooltipBg}
                        rotation={45}
                        offsetX={tooltipArrowSize / 2}
                        offsetY={tooltipArrowSize / 2}
                      />
                      <Text
                        x={tooltipPaddingX}
                        y={0}
                        width={totalTooltipWidth - tooltipPaddingX * 2}
                        height={tooltipHeight}
                        text={totalLabel}
                        fontSize={footerLabelFontSize}
                        fontFamily={fontFamily}
                        fill={tooltipFg}
                        align="center"
                        verticalAlign="middle"
                      />
                    </Group>
                  )}
                </>
              );
            })()}
          </Group>
        </>
      )}
      </Group>
      {entrance.highlightProps && <Rect ref={entrance.highlightRef} {...entrance.highlightProps} />}
    </Group>
  );
});

OrgPersonNodeInner.displayName = 'OrgPersonNode';

export const OrgPersonNode = memo(OrgPersonNodeInner);
export default OrgPersonNode;
